import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import Draggable from 'react-draggable';
import { Button } from 'react-bootstrap';

import NoData from '../../../components/NoData/NoData';
import withChartExport from '../../../components/ChartExporting';
import { saveConfig, updateAvgChart } from '../../../components/Filter/FilterActions';
import dateUtils, { getWorkMinutesPerDay, formatWorkDuration } from '../../../util/dates';
import throttle from '../../../util/throttle';

import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

const config = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff'
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: ['#00a2e3', '#007baa', '#015676'],
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function() {
      return `${this.y} day${this.y != 1 ? 's' : ''}`;
    }
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    ordinal: false,
    showLastTickLabel: true,
    title: {
      text: null
    },
    plotLines: [
      {
        value: 0
      }
    ]
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  plotOptions: {
    spline: {
      marker: {
        symbol: 'circle',
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 5
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      }
    }
  },
  series: [
    {
      data: []
    }
  ]
};

export class WipAvgAge extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isFullSreen: false,
      width: undefined,
      height: undefined,
      pos: { x: 95, y: 112 },
      wipAvgAge: 0,
      data: []
    };

    this.calculateChartData = throttle(this._calculateChartData, 500, this);
  }

  static defaultProps = {
    data: [],
    day: 0
  };

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    config.exporting = { ...config.exporting, ...this.props.chartExporting };

    let that = this;
    config.tooltip.formatter = function() {
      return that.tooltipFormatter(this, that);
    };
    this.chart = new Highcharts['Chart'](`wip_avg_age_chart_container`, config);

    if (this.props.data && this.props.data.length) {
      this.updateDateFormat(this.props.dateFormat);
      this.calculateChartData();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.calculateChartData();
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateDateFormat(this.props.dateFormat);
    }

    if (prevProps.day !== this.props.day) {
      this.setState({
        wipAvgAge:
          !!this.state.data.length && this.state.data[this.props.day]
            ? this.state.data[this.props.day].wipAvgAgeInMinutes
            : 0
      });
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateDateFormat(dateFormat) {
    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: dateUtils.createHighchartFormats(dateFormat)
      }
    });
  }

  _calculateChartData() {
    if (!this.chart || !this.chart.update || !this.chart.options) {
      return false;
    }

    const data = this.generateData(this.props.data);

    if (!data.mean.length) {
      return false;
    }

    this.chart.update({
      series: [
        {
          data: data.mean
        }
      ],
      xAxis: {
        plotLines: [
          {
            color: '#F5F5F5',
            width: 1,
            value: data.mean[0].x,
            label: {
              text: ''
            }
          }
        ]
      }
    });
  }

  generateData = data => {
    const stats = {};
    const workMinutesPerDay = getWorkMinutesPerDay(this.props.excludeWeekendOpts, this.props.useWorkingHoursInCycleTimeDays);

    let raw = _(data)
      .flatMap(i => {
        return _.flatMap(i.series, 'data');
      })
      .sortBy('lastDate')
      .groupBy(i => moment(i.lastDate).startOf('day'))
      .map((i, date) => {
        const wipAvgAgeInMinutes = _.meanBy(i, 'cycleTimeInMinutes');

        return {
          x: +moment(date).local(),
          wipAvgAgeInMinutes,
          mean: Math.ceil(wipAvgAgeInMinutes / workMinutesPerDay)
        };
      })
      .value();

    _(data)
      .filter(d => {
        return !d.series.length;
      })
      .forEach(d => {
        raw.push({
          x: +moment(d.date).local(),
          wipAvgAgeInMinutes: 0,
          mean: 0
        });
      });
    raw = _.orderBy(raw, 'x');

    this.setState({
      data: raw,
      wipAvgAge: !!raw.length ? _.last(raw).wipAvgAgeInMinutes : 0
    });

    stats.mean = _.map(raw, i => {
      return { x: i.x, y: i.mean };
    });
    return stats;
  };

  updateFontSize = () => {
    const opt = {
      exporting: {
        enabled: this.state.isFullSreen
      },
      plotOptions: {
        spline: {
          dataLabels: {
            style: {
              fontSize: this.state.isFullSreen ? '16px' : '11px',
              fontWeight: 'bold',
              color: 'contrast'
            }
          }
        }
      }
    };

    if (this.state.isFullSreen) {
      opt.yAxis = {
        gridLineWidth: 1,
        labels: {
          enabled: true
        }
      };
    } else {
      opt.yAxis = {
        gridLineWidth: 0,
        labels: {
          enabled: false
        }
      };
    }

    this.chart.update(opt);
    this.chart.reflow();
  };

  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          left: undefined,
          isFullSreen: false,
          pos: { x: 95, y: 112 }
        },
        () => this.updateFontSize()
      );
    } else {
      this.setState(
        {
          width: '100%',
          height: '100%',
          left: -10,
          isFullSreen: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  tooltipFormatter(chartScope, componentScope) {
    let tooltipHtml = '',
      s = [];
    tooltipHtml += `<div class="tooltip-label-item">${moment(
      chartScope.x
    ).format(componentScope.props.dateFormat)}</div>`;
    tooltipHtml += `<div class="tooltip-label-item">${
      chartScope.y
    } day${chartScope.y != 1 ? 's' : ''}</div>`;
    s.push(tooltipHtml);
    return s;
  }

  render() {
    return (
      <Draggable
        defaultPosition={{ x: 95, y: 112 }}
        position={this.state.pos}
        onStop={this.handleDrag}
        axis="both"
        bounds="parent"
      >
        <div
          className={`box_white_shadow chart_averages_box ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{
            width: this.state.width,
            height: this.state.height,
            left: this.state.left,
            position: this.state.isFullSreen ? 'absolute' : ''
          }}
        >
          <div className='box_white_title mb-4'>
            <h3>WIP Average Age</h3>
            <div className='right-actions'>
              <Button variant='secondary' size="sm"
                className={`btn-action ${this.state.isFullSreen ? 'fullscreen' : ''}`}
                onClick={this.handleResize}
              >
                <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          
          <p>
            {formatWorkDuration(this.state.wipAvgAge, this.props.excludeWeekendOpts, this.props.timePrecision, this.props.useWorkingHoursInCycleTimeDays)}
          </p>
          {/* <NoData loading={!this.props.allLoaded} /> */}
          <div id="wip_avg_age_chart_container" />
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateAvgChart(false, 'cta')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    timePrecision: state.filters.timePrecision,
    dateFormat: state.filters.dateFormat,
    allLoaded: state.cta.allLoaded,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(WipAvgAge));
